import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, LogOut, CreditCard, Mail, Building2, Copy, Calendar } from "lucide-react";
import { api, clientHeaders, getClientToken, setClientToken } from "../lib/api";
import { toast } from "sonner";

const STATUS_META = {
  trial:           { label: "Trial",          cls: "pill-active",   tone: "cyan" },
  active:          { label: "Active",         cls: "pill-active",   tone: "cyan" },
  payment_failed:  { label: "Payment Failed", cls: "pill-pending",  tone: "pink" },
  inactive:        { label: "Inactive",       cls: "pill-inactive", tone: "slate" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-AU", { day:"numeric", month:"short", year:"numeric"});
  } catch { return iso; }
}

function ForgotPasswordForm({ onBack, onSent }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { contact_email: email });
      onSent(email);
    } catch (err) {
      // Backend returns ok:true even on error to prevent enumeration, but
      // surface network-level issues if any
      toast.error(err?.response?.data?.detail || "Could not send reset email. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="forgot-password-form"
      className="relative so-card rounded-2xl p-8 w-full max-w-md"
    >
      <div className="uppercase text-[11px] tracking-widest text-[#06B6D4] font-mono">
        Account
      </div>
      <h1 className="mt-3 font-display text-2xl font-extrabold tracking-tight">
        Reset your password
      </h1>
      <p className="mt-2 text-[#94A3B8] text-sm">
        Enter your email address and we will send you a reset link.
      </p>

      <div className="mt-6 space-y-4">
        <div>
          <label className="block text-xs uppercase tracking-widest text-[#94A3B8] mb-2 font-mono">
            Email
          </label>
          <input
            data-testid="forgot-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            className="so-input w-full rounded-lg px-4 py-3 text-sm"
            required
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          data-testid="forgot-submit-btn"
          className="btn-blue w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-medium disabled:opacity-60"
        >
          {submitting ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Sending…
            </>
          ) : (
            "Send reset link"
          )}
        </button>
        <button
          type="button"
          onClick={onBack}
          data-testid="forgot-back-btn"
          className="block w-full text-center text-sm text-[#06B6D4] hover:underline"
        >
          Back to sign in
        </button>
      </div>
    </form>
  );
}

function ForgotPasswordSent({ email, onBack }) {
  return (
    <div
      data-testid="forgot-sent-card"
      className="relative so-card rounded-2xl p-8 w-full max-w-md text-center"
    >
      <div className="uppercase text-[11px] tracking-widest text-[#06B6D4] font-mono">
        Check your inbox
      </div>
      <h1 className="mt-3 font-display text-2xl font-extrabold tracking-tight">
        Check your inbox
      </h1>
      <p className="mt-4 text-[#94A3B8] text-sm leading-relaxed">
        We have sent a password reset link to{" "}
        <span className="text-[#06B6D4]">{email}</span>. The link expires in 1 hour.
      </p>
      <button
        type="button"
        onClick={onBack}
        data-testid="forgot-sent-back-btn"
        className="mt-6 inline-block text-sm text-[#06B6D4] hover:underline"
      >
        Back to sign in
      </button>
    </div>
  );
}

function LoginForm({ onLogin }) {
  const [view, setView] = useState("signin"); // signin | forgot | forgot-sent
  const [sentEmail, setSentEmail] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/client/login", { contact_email: email, password });
      setClientToken(data.token);
      onLogin();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign in failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-[80vh] bg-[#0A1628] text-[#F8FAFC] flex items-center justify-center py-20 px-4">
      <div className="absolute inset-0 so-grid opacity-25 pointer-events-none" />
      <div className="absolute -top-20 -right-20 w-[420px] h-[420px] so-glow-blue" />
      <div className="absolute bottom-0 left-0 w-[420px] h-[420px] so-glow-cyan" />

      {view === "forgot" && (
        <ForgotPasswordForm
          onBack={() => setView("signin")}
          onSent={(em) => {
            setSentEmail(em);
            setView("forgot-sent");
          }}
        />
      )}

      {view === "forgot-sent" && (
        <ForgotPasswordSent
          email={sentEmail}
          onBack={() => {
            setSentEmail("");
            setView("signin");
          }}
        />
      )}

      {view === "signin" && (
        <form
          onSubmit={submit}
          data-testid="client-login-form"
          className="relative so-card rounded-2xl p-8 w-full max-w-md"
        >
          <div className="uppercase text-[11px] tracking-widest text-[#06B6D4] font-mono">
            Account
          </div>
          <h1 className="mt-3 font-display text-2xl font-extrabold tracking-tight">Sign in</h1>
          <p className="mt-2 text-[#94A3B8] text-sm">
            Access your dashboard, widget, and subscription.
          </p>

          <div className="mt-6 space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-widest text-[#94A3B8] mb-2 font-mono">Email</label>
              <input
                data-testid="client-login-email"
                type="email" value={email} onChange={(e)=>setEmail(e.target.value)}
                autoComplete="email"
                className="so-input w-full rounded-lg px-4 py-3 text-sm" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs uppercase tracking-widest text-[#94A3B8] font-mono">Password</label>
                <button
                  type="button"
                  onClick={() => setView("forgot")}
                  data-testid="forgot-password-link"
                  className="text-xs text-[#06B6D4] hover:underline font-mono"
                >
                  Forgot password?
                </button>
              </div>
              <input
                data-testid="client-login-password"
                type="password" value={password} onChange={(e)=>setPassword(e.target.value)}
                autoComplete="current-password"
                className="so-input w-full rounded-lg px-4 py-3 text-sm" />
            </div>
            <button
              type="submit" disabled={submitting}
              data-testid="client-login-submit"
              className="btn-blue w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-medium disabled:opacity-60"
            >
              {submitting ? <><Loader2 size={14} className="animate-spin" /> Signing in…</> : "Sign in"}
            </button>
            <div className="text-xs text-[#94A3B8] text-center space-y-1.5">
              <div>
                New to GenQuotes?{" "}
                <Link
                  to="/get-started"
                  data-testid="signin-getstarted-link"
                  className="text-[#06B6D4] hover:underline"
                >
                  Get started
                </Link>
              </div>
              <div data-testid="signin-invite-hint">
                Already set up? Check your email for your personal signup link.
              </div>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}

function AccountPanel({ me, onLogout, onPortal, portaling }) {
  const meta = STATUS_META[me.status] || STATUS_META.inactive;
  const origin = window.location.origin;
  const embedSnippet = `<iframe src="${origin}/widget/${me.widget_id}" width="100%" height="640" frameborder="0"></iframe>`;
  const copy = (text, label) => {
    navigator.clipboard?.writeText(text);
    toast.success(`${label} copied`);
  };

  return (
    <div className="relative min-h-[80vh] bg-[#0A1628] text-[#F8FAFC] py-16">
      <div className="absolute inset-0 so-grid opacity-25 pointer-events-none" />
      <div className="relative max-w-4xl mx-auto px-6">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="uppercase text-xs tracking-widest text-[#06B6D4] font-mono">Account</div>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
              {me.business_name}
            </h1>
            <div className="mt-2 text-[#94A3B8] text-sm flex items-center gap-2">
              <Mail size={13} /> {me.contact_email}
            </div>
          </div>
          <button
            onClick={onLogout}
            data-testid="client-logout-btn"
            className="btn-ghost inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <div className="so-card rounded-xl p-5" data-testid="account-status-card">
            <div className="uppercase text-[11px] tracking-widest text-[#94A3B8] font-mono">Status</div>
            <div className="mt-3"><span className={`pill ${meta.cls}`}>● {meta.label}</span></div>
          </div>
          <div className="so-card rounded-xl p-5" data-testid="account-trial-card">
            <div className="uppercase text-[11px] tracking-widest text-[#94A3B8] font-mono">
              {me.status === "trial" ? "Trial ends" : "Next billing"}
            </div>
            <div className="mt-3 font-display text-2xl font-bold tracking-tight flex items-center gap-2">
              <Calendar size={16} className="text-[#06B6D4]" />
              {fmtDate(me.status === "trial" ? me.trial_end_date : me.next_billing_date)}
            </div>
          </div>
          <div className="so-card rounded-xl p-5" data-testid="account-plan-card">
            <div className="uppercase text-[11px] tracking-widest text-[#94A3B8] font-mono">Plan</div>
            <div className="mt-3 font-display text-2xl font-bold tracking-tight">QuoteSnap Pro</div>
            <div className="text-[#94A3B8] text-xs mt-1">$79 AUD / month</div>
          </div>
        </div>

        <div className="mt-8 so-card rounded-2xl p-8" data-testid="account-widget-card">
          <div className="flex items-center gap-2 text-[#06B6D4]">
            <Building2 size={16} /> <span className="uppercase text-[11px] tracking-widest font-mono">Your widget</span>
          </div>
          <h2 className="mt-2 font-display text-xl font-bold tracking-tight">Embed on your website</h2>
          <p className="mt-1 text-[#94A3B8] text-sm">
            Paste this snippet anywhere in your site. The widget renders live and reflects your subscription status automatically.
          </p>

          <div className="mt-5">
            <div className="uppercase text-[11px] tracking-widest text-[#94A3B8] font-mono">Widget ID</div>
            <div className="mt-2 flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-[#091324] border border-white/10">
              <code className="text-[#06B6D4] text-sm font-mono break-all">{me.widget_id}</code>
              <button onClick={() => copy(me.widget_id, "Widget ID")} className="text-[#94A3B8] hover:text-[#F8FAFC] shrink-0" aria-label="Copy">
                <Copy size={14} />
              </button>
            </div>

            <div className="mt-5 uppercase text-[11px] tracking-widest text-[#94A3B8] font-mono">Embed snippet</div>
            <div className="mt-2 flex items-start justify-between gap-3 px-4 py-3 rounded-lg bg-[#091324] border border-white/10">
              <code className="text-[#F8FAFC]/85 text-xs font-mono break-all leading-relaxed">{embedSnippet}</code>
              <button onClick={() => copy(embedSnippet, "Embed snippet")} className="text-[#94A3B8] hover:text-[#F8FAFC] shrink-0" aria-label="Copy">
                <Copy size={14} />
              </button>
            </div>
          </div>
        </div>

        <div className="mt-8 so-card rounded-2xl p-8" data-testid="account-billing-card">
          <div className="flex items-center gap-2 text-[#06B6D4]">
            <CreditCard size={16} /> <span className="uppercase text-[11px] tracking-widest font-mono">Billing</span>
          </div>
          <h2 className="mt-2 font-display text-xl font-bold tracking-tight">Manage subscription</h2>
          <p className="mt-1 text-[#94A3B8] text-sm">
            Update your card, view invoices, or cancel at any time through the secure Stripe customer portal.
          </p>
          <button
            onClick={onPortal}
            disabled={portaling}
            data-testid="account-portal-btn"
            className="mt-5 btn-blue inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-medium disabled:opacity-60"
          >
            {portaling ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
            Manage subscription
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Account() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [portaling, setPortaling] = useState(false);

  const load = async () => {
    if (!getClientToken()) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/client/me", { headers: clientHeaders() });
      setMe(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        setClientToken(null);
        setMe(null);
      } else {
        toast.error("Failed to load account");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Surface a one-time success toast after a successful password reset
    const params = new URLSearchParams(window.location.search);
    if (params.get("msg") === "password-reset") {
      toast.success("Password updated — please sign in with your new password");
      params.delete("msg");
      const newSearch = params.toString();
      window.history.replaceState(
        {},
        "",
        window.location.pathname + (newSearch ? `?${newSearch}` : "")
      );
    }
    // eslint-disable-next-line
  }, []);

  const onLogout = () => {
    setClientToken(null);
    setMe(null);
    navigate("/account", { replace: true });
  };

  const onPortal = async () => {
    setPortaling(true);
    try {
      const { data } = await api.post(
        "/client/billing-portal",
        { origin_url: window.location.origin },
        { headers: clientHeaders() }
      );
      if (data?.url) window.location.href = data.url;
      else toast.info("Portal not available yet.");
    } catch (err) {
      toast.info(err?.response?.data?.detail || "Stripe Customer Portal is not yet enabled.");
    } finally {
      setPortaling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-[#94A3B8]">
        <Loader2 className="animate-spin mr-2" size={16} /> Loading…
      </div>
    );
  }

  if (!me) return <LoginForm onLogin={load} />;
  return <AccountPanel me={me} onLogout={onLogout} onPortal={onPortal} portaling={portaling} />;
}
